import { Injectable } from '@angular/core';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { BannerConfig, isBannerConfig } from '@examdojo/core/ui/banner';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import { distinctUntilDeepChanged } from '@examdojo/rxjs';
import { combineLatest, distinctUntilChanged, map, Observable, shareReplay } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class InformativeBannerService {
  constructor(private readonly featureFlagService: FeatureFlagService<ExamdojoFeatureFlags>) {}

  readonly bannerConfigs$: Observable<Array<{ key: string; config: BannerConfig }>> = combineLatest([
    this.featureFlagService.select(ExamDojoFeatureFlag.InfoBanner),
    this.featureFlagService.selectByPattern(/^banner_/),
  ]).pipe(
    map(([infoBannerValue, patternBanners]) => {
      const result: Array<{ key: string; config: BannerConfig }> = [];

      const legacyConfig = this.toBannerConfig(infoBannerValue);
      if (legacyConfig) {
        result.push({ key: ExamDojoFeatureFlag.InfoBanner, config: legacyConfig });
      }

      for (const { key, value } of patternBanners) {
        // Skip the legacy InfoBanner flag — it's handled separately via select() above.
        // This guard relies on its enum value ("info_banner") not matching /^banner_/.
        if (key === ExamDojoFeatureFlag.InfoBanner) {
          continue;
        }
        const config = this.toBannerConfig(value);
        if (config) {
          result.push({ key, config });
        }
      }

      return result;
    }),
    distinctUntilDeepChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly enabled$: Observable<boolean> = this.bannerConfigs$.pipe(
    map((configs) => configs.length > 0),
    distinctUntilChanged(),
  );

  private toBannerConfig(flagValue: unknown): BannerConfig | null {
    if (typeof flagValue === 'string') {
      return flagValue.trim().length > 0 ? { message: flagValue } : null;
    }
    if (isBannerConfig(flagValue)) {
      return flagValue;
    }
    return null;
  }
}
